import { Action } from 'redux';
import { ContractState, ContractUnloadState } from '../../states/ContractState';
import * as Actions from '../../actions';
import * as ActionEvents from '../../actions/ContractStageItem.Actions';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractStage } from '../../../models/Entities/Contracts/ContractStage';

type ContractStageItemViewModel = {
    id?: number;
    contractStageId?: number;
    ermProtocolVisitId?: number;
    emrProtocolVisitName: string;
};

type KnownAction =
    | Actions.ContractStageItems.ContractStageItemGetAllRequestAction
    | Actions.ContractStageItems.ContractStageItemGetAllSuccessAction
    | Actions.ContractStageItems.ContractStageItemGetAllFailureAction
    | Actions.ContractStageItems.ContractStageItemGetByIdRequestAction
    | Actions.ContractStageItems.ContractStageItemGetByIdSuccessAction
    | Actions.ContractStageItems.ContractStageItemGetByIdFailureAction
    | Actions.ContractStageItems.ContractStageItemCreateRequestAction
    | Actions.ContractStageItems.ContractStageItemCreateSuccessAction
    | Actions.ContractStageItems.ContractStageItemCreateFailureAction
    | Actions.ContractStageItems.ContractStageItemUpdateRequestAction
    | Actions.ContractStageItems.ContractStageItemUpdateSuccessAction
    | Actions.ContractStageItems.ContractStageItemUpdateFailureAction
    | Actions.ContractStageItems.ContractStageItemDeleteRequestAction
    | Actions.ContractStageItems.ContractStageItemDeleteSuccessAction
    | Actions.ContractStageItems.ContractStageItemDeleteFailureAction;

const updateNestedStageItems = (
    contracts: Contract[] | undefined,
    contractStageId: number,
    updater: (currentItems: ContractStageItemViewModel[]) => ContractStageItemViewModel[]
): Contract[] | undefined => {
    if (!contracts) return contracts;

    let hasChanges = false;

    const updatedContracts = contracts.map((contract: Contract) => {
        let contractChanged = false;

        const updatedAddendums = (contract.addendums || []).map((addendum: ContractAddendum) => {
            let addendumChanged = false;

            const updatedStages = (addendum.stages || []).map((stage: ContractStage) => {
                if (stage.id !== contractStageId) {
                    return stage;
                }

                const currentItems = stage.contractStageItems || [];
                const nextItems = updater(currentItems);

                hasChanges = true;
                addendumChanged = true;

                return {
                    ...stage,
                    contractStageItems: nextItems
                } as ContractStage;
            });

            if (!addendumChanged) {
                return addendum;
            }

            contractChanged = true;

            return {
                ...addendum,
                stages: updatedStages
            } as ContractAddendum;
        });

        if (!contractChanged) {
            return contract;
        }

        return {
            ...contract,
            addendums: updatedAddendums
        } as Contract;
    });

    return hasChanges ? updatedContracts : contracts;
};

const upsertStageItem = (
    items: ContractStageItemViewModel[],
    stageItem: ContractStageItemViewModel
): ContractStageItemViewModel[] => {
    if (stageItem.id === undefined || stageItem.id === null) {
        return [...items, stageItem];
    }

    const exists = items.some((item: ContractStageItemViewModel) => item.id === stageItem.id);
    if (exists) {
        return [
            ...items.filter((item: ContractStageItemViewModel) => item.id !== stageItem.id),
            stageItem
        ];
    }

    return [...items, stageItem];
};

export default function ContractStageItemsReducer(
    state: ContractState | undefined,
    incomingAction: Action
): ContractState {
    const currentState = state ?? ContractUnloadState;

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.CONTRACTSTAGEITEM_GET_ALL_REQUEST:
        case ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_REQUEST:
        case ActionEvents.CONTRACTSTAGEITEM_CREATE_REQUEST:
        case ActionEvents.CONTRACTSTAGEITEM_UPDATE_REQUEST:
        case ActionEvents.CONTRACTSTAGEITEM_DELETE_REQUEST:
            return {
                ...currentState,
                error: undefined
            };

        case ActionEvents.CONTRACTSTAGEITEM_GET_ALL_SUCCESS:
            return {
                ...currentState,
                list: updateNestedStageItems(
                    currentState.list,
                    action.contractStageId,
                    () => action.payload
                ),
                error: undefined
            };

        case ActionEvents.CONTRACTSTAGEITEM_GET_ALL_FAILURE:
        case ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_FAILURE:
        case ActionEvents.CONTRACTSTAGEITEM_CREATE_FAILURE:
        case ActionEvents.CONTRACTSTAGEITEM_UPDATE_FAILURE:
        case ActionEvents.CONTRACTSTAGEITEM_DELETE_FAILURE:
            return {
                ...currentState,
                error: action.error
            };

        case ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_SUCCESS:
        case ActionEvents.CONTRACTSTAGEITEM_CREATE_SUCCESS:
        case ActionEvents.CONTRACTSTAGEITEM_UPDATE_SUCCESS: {
            return {
                ...currentState,
                list: updateNestedStageItems(
                    currentState.list,
                    action.contractStageId,
                    (items: ContractStageItemViewModel[]) => upsertStageItem(items, action.payload)
                ),
                error: undefined
            };
        }

        case ActionEvents.CONTRACTSTAGEITEM_DELETE_SUCCESS:
            return {
                ...currentState,
                list: updateNestedStageItems(
                    currentState.list,
                    action.contractStageId,
                    (items: ContractStageItemViewModel[]) =>
                        items.filter((item: ContractStageItemViewModel) => item.id !== action.stageItemId)
                ),
                error: undefined
            };

        default:
            return currentState;
    }
}
